import { useState, useMemo } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons'

import { ChartLoadingPlaceholder } from './ChartLoadingPlaceholder'
import { CollapsibleSection } from '../CollapsibleSection'
import { Viewport } from '../../common/types'
import { DeviceSensorData } from '../../hooks/useMultiDeviceReadings'
import { CompareVariant } from '../../contexts/AppContext'
import { ComparableSensorType, COMPARABLE_SENSOR_TYPES, SENSOR_SUB_TYPES, SensorSubType } from '../SensorTypeSelector'
import { CompareLayout, ZoomWindow, isCombined, isSeparated, sharedYRange } from '../../utils/compareView'
import { CompareSeries, buildCompareSeries } from './compareSeries'
import { CompareGroupChart, SensorsMenu } from './CompareGroupChart'
import { useSyncedCharts } from './useSyncedCharts'

// One chart on its own gets the full height; a stack uses shorter charts
const SOLO_CHART_HEIGHT = 450
const STACKED_CHART_HEIGHT = 260

interface ComparisonChartProps {
  sensorType: ComparableSensorType | null
  subType?: SensorSubType | null
  deviceSensorData: DeviceSensorData[]
  viewport: Viewport
  isLoading: boolean
  variant?: CompareVariant
  // Layout and shared zoom live in ChartsPage (this component remounts on refetch)
  layout: CompareLayout
  onSplitOut: (id: string) => void
  onMoveInto: (id: string, chartIndex: number) => void
  onMoveToNewChart: (id: string) => void
  onRemoveChart: (chartIndex: number) => void
  onCombineAll: () => void
  onSeparateAll: () => void
  zoomWindow: ZoomWindow | null
  onZoomChange: (zoom: ZoomWindow | null) => void
  showSplitNotice: boolean
  onDismissNotice: () => void
}

/**
 * Compare Mode charts: the selected series arranged into one or more charts on
 * a shared time axis. Each series lives in exactly one chart; chips move it
 * out to its own chart or into another one, and "Combine all" / "Separate all"
 * jump to the two extremes. Zoom, pan, reset, crosshair and tooltip are linked
 * across every chart.
 */
export function ComparisonChart({
  sensorType,
  subType,
  deviceSensorData,
  viewport,
  isLoading,
  variant = 'single',
  layout,
  onSplitOut,
  onMoveInto,
  onMoveToNewChart,
  onRemoveChart,
  onCombineAll,
  onSeparateAll,
  zoomWindow,
  onZoomChange,
  showSplitNotice,
  onDismissNotice,
}: ComparisonChartProps) {
  const isMixed = variant === 'mixed'
  const sync = useSyncedCharts(onZoomChange)

  // "Add chart" placeholder: a blank chart waiting for its first sensor
  const [showBlankChart, setShowBlankChart] = useState(false)

  // Get sensor type config for title
  const sensorTypeConfig = COMPARABLE_SENSOR_TYPES.find(t => t.key === sensorType)

  // Determine chart title and type based on sub-type / variant
  const { chartTitle, chartType } = useMemo(() => {
    if (isMixed) {
      return { chartTitle: 'Mixed Sensors', chartType: 'line' }
    }
    if (subType) {
      const rainfallSubType = SENSOR_SUB_TYPES.rainfall.find(s => s.key === subType)
      const flowSubType = SENSOR_SUB_TYPES.flowMeter.find(s => s.key === subType)
      const subTypeConfig = rainfallSubType || flowSubType

      if (subTypeConfig) {
        const baseLabel = sensorTypeConfig?.label || 'Sensor'
        return {
          chartTitle: `${baseLabel} - ${subTypeConfig.label}`,
          chartType: subTypeConfig.chartType,
        }
      }
    }
    return {
      chartTitle: sensorTypeConfig?.label || 'Comparison',
      chartType: 'line',
    }
  }, [isMixed, subType, sensorTypeConfig])

  const compareSeries = useMemo(() => buildCompareSeries(deviceSensorData, isMixed), [deviceSensorData, isMixed])

  // Each chart's members, and every series with the chart it lives in (for the menus)
  const { groups, placement } = useMemo(() => {
    const byId = new Map(compareSeries.map(s => [s.id, s]))
    const charts = layout
      .map(group => group.map(id => byId.get(id)).filter((s): s is CompareSeries => !!s))
      .filter(members => members.length > 0)
    const chartOf = new Map<string, number>()
    charts.forEach((members, i) => members.forEach(m => chartOf.set(m.id, i)))
    return {
      groups: charts.map(members => ({ members })),
      placement: compareSeries.map(series => ({ series, chartIndex: chartOf.get(series.id) ?? 0 })),
    }
  }, [layout, compareSeries])

  // Single-type charts share one Y range (across all charts) so
  // they compare by height; mixed charts each pick their own axes.
  const yRange = useMemo(() => {
    if (isMixed) return null
    return sharedYRange(
      compareSeries.filter(s => s.data.hasData).map(s => ({ readings: s.data.readings, reversed: s.data.reverseYAxis }))
    )
  }, [compareSeries, isMixed])

  const axisMin = zoomWindow?.min ?? viewport.minimum
  const axisMax = zoomWindow?.max ?? viewport.maximum
  const chartHeight = groups.length === 1 ? SOLO_CHART_HEIGHT : STACKED_CHART_HEIGHT


  const hasAnyData = deviceSensorData.some(d => d.hasData)
  const allIds = compareSeries.map(s => s.id)
  const combined = isCombined(layout)
  const separated = isSeparated(layout)

  return (
    <CollapsibleSection title={chartTitle} open={true} isLoading={isLoading}>
      {/* Layout shortcuts + shared zoom controls */}
      <div className="flex flex-wrap items-center gap-3 px-4 pt-3">
        {allIds.length > 1 && (
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs font-medium">
            <button
              type="button"
              onClick={onCombineAll}
              disabled={combined}
              className={combined ? 'px-3 py-1 bg-gray-700 text-white' : 'px-3 py-1 bg-white text-gray-700 hover:bg-gray-100'}
            >
              Combine all
            </button>
            <button
              type="button"
              onClick={onSeparateAll}
              disabled={separated}
              className={
                separated
                  ? 'px-3 py-1 border-l border-gray-300 bg-gray-700 text-white'
                  : 'px-3 py-1 border-l border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
              }
            >
              Separate all
            </button>
          </div>
        )}
        {groups.length > 1 && <span className="text-xs text-gray-500">Zoom is linked across charts</span>}
        {zoomWindow && (
          <button
            type="button"
            onClick={() => onZoomChange(null)}
            className="ml-auto px-2 py-1 rounded text-xs font-medium text-indigo-600 hover:bg-indigo-50"
          >
            Reset all charts
          </button>
        )}
      </div>

      {showSplitNotice && (
        <div className="mx-4 mt-2 flex items-center justify-between gap-3 rounded bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
          <span>Showing sensors separately. Use Combine all, or a chart's Sensors menu, to put them on one chart.</span>
          <button type="button" onClick={onDismissNotice} className="font-medium hover:underline">
            Got it
          </button>
        </div>
      )}

      {isLoading ? (
        <ChartLoadingPlaceholder />
      ) : hasAnyData ? (
        <div>
          {groups.map((g, i) => (
            <CompareGroupChart
              key={g.members.map(m => m.id).join('|')}
              index={i}
              members={g.members}
              all={placement}
              chartTitle={chartTitle}
              chartType={chartType}
              isMixed={isMixed}
              yRange={yRange}
              axisMin={axisMin}
              axisMax={axisMax}
              height={chartHeight}
              onSplitOut={onSplitOut}
              onMoveInto={onMoveInto}
              onRemove={groups.length > 1 ? onRemoveChart : undefined}
              register={sync.register}
              rangeChanged={sync.rangeChanged}
            />
          ))}

          {allIds.length > 1 &&
            (showBlankChart ? (
              <section className="border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pt-3">
                  <h3 className="text-sm font-semibold text-gray-800">New chart</h3>
                  <SensorsMenu
                    all={placement}
                    chartIndex={null}
                    memberCount={0}
                    label="Pick a sensor"
                    onPick={id => {
                      onMoveToNewChart(id)
                      setShowBlankChart(false)
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowBlankChart(false)}
                    aria-label="Discard new chart"
                    title="Discard new chart"
                    className="ml-auto px-2 py-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
                <div
                  className="mx-4 my-3 flex items-center justify-center rounded border border-dashed border-gray-300 text-sm text-gray-400"
                  style={{ height: STACKED_CHART_HEIGHT }}
                >
                  Pick a sensor to start this chart. Add more from its Sensors menu.
                </div>
              </section>
            ) : (
              <div className="px-4 py-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowBlankChart(true)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded border border-dashed border-gray-300 py-2 text-sm text-gray-500 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-xs" />
                  Add chart
                </button>
              </div>
            ))}
        </div>
      ) : (
        <div className="flex items-center justify-center py-12">
          <div className="bg-red-500 text-white font-bold rounded px-4 py-2 h-[70px] w-[320px] flex items-center justify-center">
            No data available for the selected period
          </div>
        </div>
      )}
    </CollapsibleSection>
  )
}
